import React, { useEffect, useRef, useState } from 'react';
import { FaPaperPlane, FaPaperclip, FaImage, FaVideo, FaFileAlt, FaMicrophone, FaDownload, FaSyncAlt, FaArrowLeft, FaInfoCircle, FaSearch, FaTimes, FaSpinner, FaTrash } from 'react-icons/fa';
import api from '../../utils/axiosInstance';
import { useModal } from '../../context/ModalContext';
import { socket } from '../../socket';
import { buildAttachmentPreview, getAttachmentKind, getLeadTemperature } from '../../utils/uniboxComposer';

// Safely format a date from any field the backend might provide
function formatTime(msg) {
  const raw = msg.timestamp || msg.createdAt || msg.updatedAt;
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Check if content is just a media placeholder like [document], [image], [video], etc.
function isMediaPlaceholderText(text) {
  if (!text) return false;
  return /^\[(image|video|audio|document|sticker)\]$/i.test(text.trim());
}

export default function ChatWindow({ selectedClient, chat, setChat, sessionId, onSend, onRefresh, displayName, onBack, onToggleDetail, onDeleteConversation, leadStage, contact, pipelineStages = [], onLoadMore, hasMore, loadingMore }) {
  const modal = useModal();
  const [message, setMessage] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [chatSearch, setChatSearch] = useState('');
  const [showChatSearch, setShowChatSearch] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentPreview = mediaFile ? buildAttachmentPreview(mediaFile) : null;
  const stageMeta = leadStage ? pipelineStages.find((stage) => stage.key === leadStage) : null;
  const temperature = contact?.isLead ? getLeadTemperature(contact.leadScore || 0) : null;

  // Listen for typing indicator
  useEffect(() => {
    if (!selectedClient) return;
    const handler = ({ contact, isTyping: typing }) => {
      if (contact === selectedClient) setIsTyping(typing);
    };
    socket.on('typingUpdate', handler);
    return () => { socket.off('typingUpdate', handler); setIsTyping(false); };
  }, [selectedClient]);

  // Listen for message status updates (sent → delivered → read)
  useEffect(() => {
    if (!selectedClient) return;
    const handler = ({ messageId, status }) => {
      setChat(prev => prev.map(msg =>
        (msg.whatsappMessageId === messageId || msg.messageId === messageId || msg.id === messageId)
          ? { ...msg, status }
          : msg
      ));
    };
    socket.on('messageStatusUpdate', handler);
    return () => socket.off('messageStatusUpdate', handler);
  }, [selectedClient, setChat]);

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try { await onRefresh(); } catch (e) {}
    setTimeout(() => setRefreshing(false), 600);
  };

  const setAttachmentFromFile = (file) => {
    if (!file) return;
    if (!getAttachmentKind(file)) {
      modal.alert('Unsupported attachment. Use images, videos, audio, PDF, Word, or Excel files.', 'error');
      return;
    }
    setMediaFile(file);
  };

  const handleAttachmentDrop = (e) => {
    e.preventDefault();
    setIsDraggingAttachment(false);
    setAttachmentFromFile(e.dataTransfer.files?.[0]);
  };

  const handleAttachmentDragOver = (e) => {
    if (!selectedClient) return;
    e.preventDefault();
    setIsDraggingAttachment(true);
  };

  const handleAttachmentDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingAttachment(false);
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat]);

  const handleSend = async () => {
    if (isSending) return;
    if (!message.trim() && !mediaFile) return;

    setIsSending(true);
    try {
      if (mediaFile) {
        const mediaType = getAttachmentKind(mediaFile);
        if (!mediaType) {
          modal.alert('Unsupported attachment. Use images, videos, audio, PDF, Word, or Excel files.', 'error');
          return;
        }
        const uploadResult = await uploadMediaToTempUrl(mediaFile);
        if (!uploadResult.ok) {
          modal.alert(`Upload failed: ${uploadResult.error || 'Unknown error'}`, 'error');
          return;
        }
        const fileUrl = uploadResult.url;

        await api.post(`/api/whatsapp/${sessionId}/send-media`, {
          number: selectedClient.replace(/@s\.whatsapp\.net|@c\.us/, ''),
          fileUrl,
          caption: message
        });

        // Add to chat locally with proper media object
        setChat(prev => [...prev, {
          sender: sessionId,
          recipient: selectedClient,
          content: message || '',
          direction: 'out',
          timestamp: new Date().toISOString(),
          media: { type: mediaType, url: fileUrl, caption: message || '' }
        }]);
        setMediaFile(null);
        setMessage('');
      } else {
        setMessage('');
        await onSend(message);
      }
    } finally {
      setIsSending(false);
    }
  };

  const uploadMediaToTempUrl = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/upload', formData);
      if (!res.data?.url) {
        return { ok: false, error: res.data?.error || 'No URL returned by server' };
      }
      return { ok: true, url: res.data.url };
    } catch (err) {
      console.error('Upload error:', err);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      const msg = serverMsg
        ? `${status ? `(${status}) ` : ''}${serverMsg}`
        : (err?.message || 'Network error');
      return { ok: false, error: msg };
    }
  };

  const renderMedia = (msg) => {
    if (!msg.media || !msg.media.type) return null;
    const { type, url } = msg.media;
    const isOut = msg.direction === 'out';

    // Has actual URL — render real media
    if (url) {
      if (type === 'image') {
        return (
          <div className="mb-1 cursor-pointer" onClick={() => setLightboxUrl(url)}>
            <img
              src={url}
              alt="img"
              className="max-w-full max-h-[280px] rounded-lg object-cover"
              loading="lazy"
            />
          </div>
        );
      }
      if (type === 'video') {
        return (
          <div className="mb-1">
            <video
              src={url}
              controls
              preload="metadata"
              className="max-w-full max-h-[280px] rounded-lg"
            />
          </div>
        );
      }
      if (type === 'audio') {
        return (
          <div className="mb-1">
            <audio controls src={url} className="w-full max-w-[260px]" preload="metadata" />
          </div>
        );
      }
      if (type === 'document') {
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`flex items-center gap-2.5 p-2.5 rounded-lg mb-1 transition-colors ${
              isOut
                ? 'bg-white/15 hover:bg-white/25'
                : 'bg-surface-bg hover:bg-surface-border/50'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isOut ? 'bg-white/20' : 'bg-red-50'
            }`}>
              <FaFileAlt size={16} className={isOut ? 'text-white' : 'text-red-500'} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-medium truncate ${isOut ? 'text-white' : 'text-ink-primary'}`}>
                Document
              </div>
              <div className={`text-[10px] ${isOut ? 'text-white/60' : 'text-ink-faint'}`}>
                PDF / DOC
              </div>
            </div>
            <FaDownload size={12} className={isOut ? 'text-white/70' : 'text-ink-muted'} />
          </a>
        );
      }
      if (type === 'sticker') {
        return (
          <div className="mb-1">
            <img src={url} alt="sticker" className="w-[120px] h-[120px] object-contain" loading="lazy" />
          </div>
        );
      }
    }

    // No URL — show placeholder card
    const icons = { image: FaImage, video: FaVideo, audio: FaMicrophone, document: FaFileAlt, sticker: FaImage };
    const labels = { image: 'Photo', video: 'Video', audio: 'Voice Message', document: 'Document', sticker: 'Sticker' };
    const Icon = icons[type] || FaFileAlt;
    const label = labels[type] || 'Media';

    return (
      <div className={`flex items-center gap-2.5 p-2.5 rounded-lg mb-1 ${
        isOut ? 'bg-white/15' : 'bg-surface-bg'
      }`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isOut ? 'bg-white/20' : 'bg-gray-100'
        }`}>
          <Icon size={16} className={isOut ? 'text-white/80' : 'text-ink-muted'} />
        </div>
        <div>
          <div className={`text-xs font-medium ${isOut ? 'text-white/90' : 'text-ink-secondary'}`}>
            {label}
          </div>
          <div className={`text-[10px] ${isOut ? 'text-white/50' : 'text-ink-faint'}`}>
            Media not available
          </div>
        </div>
      </div>
    );
  };

  // Build display content — filter out media placeholder text like [document]
  const getContent = (msg) => {
    // Prefer caption from media
    const caption = msg.media?.caption;

    // Get text content
    const text = msg.contentText || msg.content || '';

    // If text is just a placeholder like [document], [image] — don't show it
    if (isMediaPlaceholderText(text)) {
      return caption || null;
    }

    // If we have a caption and the text equals the caption, just return once
    if (caption && text === caption) return caption;

    return text || caption || null;
  };

  const statusIcon = (msg) => {
    const s = msg.status || msg.tick;
    // Mirror WhatsApp's own four states so users read it the same way.
    // read (ack 4-5): blue double tick.
    if (s === 'read') return <span className="text-blue-400">✓✓</span>;
    // delivered (ack 3): recipient device has it — grey double tick.
    if (s === 'delivered') return '✓✓';
    // server_ack (ack 1-2): WhatsApp servers accepted it = it left your
    // phone. Single grey tick. Legacy 'sent' rows map here too.
    if (s === 'server_ack' || s === 'sent')
      return <span className="text-white/40" title="Sent — WhatsApp received it from your phone">✓</span>;
    // Terminal non-delivery: handed to WhatsApp but no server_ack ever
    // arrived (typically a half-dead / zombie pairing). Show the truth.
    if (s === 'failed')
      return (
        <span
          className="text-red-400"
          title="Not delivered — your WhatsApp number has a bad connection. Reconnect it from Linked Devices."
        >
          ✕ not delivered
        </span>
      );
    // dispatched / pending / unknown: handed to the socket, NOT yet
    // confirmed by WhatsApp. Clock — never a tick that implies it was sent.
    return <span className="text-white/40" title="Sending… (not yet confirmed by WhatsApp)">◷</span>;
  };

  return (
    <div
      className="relative flex-1 flex flex-col min-w-0 overflow-hidden h-full"
      onDrop={handleAttachmentDrop}
      onDragOver={handleAttachmentDragOver}
      onDragLeave={handleAttachmentDragLeave}
    >
      {isDraggingAttachment && selectedClient && (
        <div className="absolute inset-3 z-30 rounded-xl border-2 border-dashed border-brand bg-brand-50/95 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <FaPaperclip size={28} className="mx-auto text-brand mb-2" />
            <div className="text-sm font-semibold text-ink-primary">Drop to attach</div>
            <div className="text-xs text-ink-muted">Images, PDFs, docs, video, and audio are supported.</div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="border-b border-surface-border px-4 py-3 bg-surface-card flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {selectedClient && onBack && (
            <button
              onClick={onBack}
              className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-bg text-ink-muted transition-colors flex-shrink-0"
            >
              <FaArrowLeft size={14} />
            </button>
          )}
          <span className="font-semibold text-sm text-ink-primary truncate">
            {selectedClient ? (displayName || selectedClient) : 'Select a client to view messages'}
          </span>
          {leadStage && (
            <span
              className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium text-white flex-shrink-0"
              style={{ backgroundColor: stageMeta?.color || '#9CA3AF' }}
            >
              {stageMeta?.label || leadStage}
            </span>
          )}
        </div>
        {selectedClient && (
          <div className="flex items-center gap-1 flex-shrink-0">
          {onToggleDetail && (
            <button
              onClick={onToggleDetail}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-ink-muted hover:text-brand hover:bg-surface-bg transition-colors border border-surface-border"
              title="Lead info"
            >
              <FaInfoCircle size={13} />
              <span className="hidden sm:inline">Info</span>
            </button>
          )}
          <button
            onClick={() => { setShowChatSearch(v => !v); setChatSearch(''); }}
            className={`flex items-center gap-1.5 text-xs transition-colors px-2 py-1 rounded-md hover:bg-surface-bg ${showChatSearch ? 'text-brand' : 'text-ink-muted hover:text-brand'}`}
            title="Search in chat"
          >
            <FaSearch size={11} />
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-brand transition-colors px-2 py-1 rounded-md hover:bg-surface-bg"
            title="Fetch latest messages"
          >
            <FaSyncAlt size={11} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          {onDeleteConversation && (
            <button
              onClick={onDeleteConversation}
              className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-red-600 transition-colors px-2 py-1 rounded-md hover:bg-red-50"
              title="Delete conversation (clears history and resets AI)"
            >
              <FaTrash size={11} />
            </button>
          )}
          </div>
        )}
      </div>

      {selectedClient && contact && (
        <div className="border-b border-surface-border bg-surface-card px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`px-2 py-1 rounded-lg text-[11px] font-semibold ${
              contact.isLead ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {contact.isLead ? 'CRM lead' : 'Conversation'}
            </span>
            {temperature && (
              <span className={`px-2 py-1 rounded-lg text-[11px] font-semibold ${
                temperature.key === 'hot'
                  ? 'bg-red-50 text-red-600'
                  : temperature.key === 'warm'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-slate-100 text-slate-600'
              }`}>
                {temperature.label} score {contact.leadScore || 0}
              </span>
            )}
            {contact.source && (
              <span className="px-2 py-1 rounded-lg text-[11px] bg-surface-bg text-ink-muted border border-surface-border capitalize">
                {contact.source}
              </span>
            )}
            {(contact.tags || []).slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-1 rounded-lg text-[11px] bg-surface-bg text-ink-muted border border-surface-border">
                {tag}
              </span>
            ))}
          </div>
          {onToggleDetail && (
            <button
              onClick={onToggleDetail}
              className="text-xs font-medium text-brand hover:text-brand-700"
            >
              Open CRM panel
            </button>
          )}
        </div>
      )}

      {/* Search bar */}
      {showChatSearch && (
        <div className="px-3 py-2 border-b border-surface-border bg-surface-card flex items-center gap-2">
          <FaSearch size={11} className="text-ink-faint flex-shrink-0" />
          <input
            type="text"
            value={chatSearch}
            onChange={e => setChatSearch(e.target.value)}
            placeholder="Search messages..."
            className="flex-1 text-sm bg-transparent outline-none text-ink-primary placeholder:text-ink-faint"
            autoFocus
          />
          {chatSearch && (
            <span className="text-[10px] text-ink-muted flex-shrink-0">
              {chat.filter(m => (m.content || m.contentText || '').toLowerCase().includes(chatSearch.toLowerCase())).length} found
            </span>
          )}
          <button onClick={() => { setShowChatSearch(false); setChatSearch(''); }} className="text-ink-faint hover:text-ink-primary">
            <FaTimes size={12} />
          </button>
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 overflow-auto p-4 bg-surface-bg">
        {hasMore && (
          <div className="text-center mb-3">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="text-xs text-brand hover:text-brand-700 transition-colors px-3 py-1.5 rounded-lg bg-surface-card border border-surface-border hover:bg-surface-bg"
            >
              {loadingMore ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}
        {chat.length === 0 && (
          <div className="text-sm text-ink-muted text-center py-8">No messages yet.</div>
        )}
        {chat.map((msg, i) => {
          const content = getContent(msg);
          const hasMedia = msg.media && msg.media.type;
          // Skip completely empty messages (no text, no media)
          if (!content && !hasMedia) return null;

          // Check if message matches search
          const searchQ = chatSearch.toLowerCase();
          const matchesSearch = !searchQ || (content || '').toLowerCase().includes(searchQ);

          return (
            <div
              key={msg._id || msg.messageId || i}
              className={`mb-3 flex ${msg.direction === 'out' ? 'justify-end' : 'justify-start'} ${searchQ && !matchesSearch ? 'opacity-20' : ''} transition-opacity`}
            >
              <div
                className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${searchQ && matchesSearch ? 'ring-2 ring-yellow-400 ' : ''}${
                  msg.direction === 'out'
                    ? 'bg-brand text-white rounded-br-sm'
                    : 'bg-surface-card border border-surface-border text-ink-primary rounded-bl-sm'
                }`}
              >
                {renderMedia(msg)}
                {content && (
                  <div className="whitespace-pre-wrap break-words">{content}</div>
                )}
                <div className={`text-[10px] mt-1 ${msg.direction === 'out' ? 'text-white/70' : 'text-ink-faint'} flex items-center gap-1`}>
                  {formatTime(msg)}
                  {msg.direction === 'out' && (
                    <span>{statusIcon(msg)}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {isTyping && (
          <div className="mb-3 flex justify-start">
            <div className="px-4 py-2.5 rounded-xl bg-surface-card border border-surface-border rounded-bl-sm">
              <div className="flex gap-1 items-center">
                <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-ink-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef}></div>
      </div>

      {/* Image Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="preview"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}

      {/* Input area */}
      {selectedClient && (
        <div className="border-t border-surface-border p-3 bg-surface-card">
          {attachmentPreview && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-surface-border bg-surface-bg px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white border border-surface-border flex items-center justify-center flex-shrink-0">
                  {attachmentPreview.kind === 'image' ? (
                    <FaImage size={15} className="text-emerald-600" />
                  ) : attachmentPreview.kind === 'video' ? (
                    <FaVideo size={15} className="text-blue-600" />
                  ) : attachmentPreview.kind === 'audio' ? (
                    <FaMicrophone size={15} className="text-purple-600" />
                  ) : (
                    <FaFileAlt size={15} className="text-red-500" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-ink-primary truncate">{attachmentPreview.name}</div>
                  <div className="text-[10px] text-ink-muted">{attachmentPreview.label} · {attachmentPreview.size}</div>
                </div>
              </div>
              <button className="text-ink-faint hover:text-red-500" onClick={() => setMediaFile(null)} title="Remove attachment">
                <FaTimes size={12} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              className="btn-icon text-ink-muted hover:text-ink-primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <FaPaperclip size={16} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => {
                setAttachmentFromFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <textarea
              rows={1}
              className="input flex-1 text-sm resize-none max-h-32"
              placeholder={mediaFile ? 'Add a caption... (Shift+Enter for new line)' : 'Type your message... (Shift+Enter for new line)'}
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              className="w-9 h-9 rounded-lg bg-brand text-white flex items-center justify-center hover:bg-brand-700 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSend}
              disabled={isSending}
              title="Send"
            >
              {isSending ? <FaSpinner size={14} className="animate-spin" /> : <FaPaperPlane size={14} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
